import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { emitCopilotMutation } from "../mutations";
import { withFollow } from "./followHelpers";

const PAYMENT_METHODS = ["cash", "card", "transfer", "courtesy"] as const;

export const closeAllForTableSkill: Skill = {
  id: "closeAllForTable",
  description:
    "Cierra TODOS los pedidos activos de una mesa con un único cobro (multi-rondas en una sola transacción).",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      tableId: { type: "string" },
      cashRegisterId: { type: "string" },
      paymentMethod: { type: "string", enum: [...PAYMENT_METHODS] },
      paymentDetails: { type: "string" },
      clientId: { type: "string" },
    },
    required: ["restaurantId", "tableId", "cashRegisterId", "paymentMethod"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const tableId = String(args.tableId ?? "");
    const cashRegisterId = String(args.cashRegisterId ?? "");
    const paymentMethod = String(args.paymentMethod ?? "");
    if (!restaurantId || !tableId || !cashRegisterId || !paymentMethod) {
      return { ok: false, error: "Faltan datos." };
    }
    const paymentDetails = args.paymentDetails ? String(args.paymentDetails) : undefined;
    const clientId = args.clientId ? String(args.clientId) : undefined;

    const ok = await ctx.confirmation.confirm({
      title: "Cerrar todos los pedidos de la mesa",
      description:
        "Vas a cobrar TODAS las rondas activas de esta mesa con un único pago. IRREVERSIBLE.",
      details: [
        { label: "Mesa", value: tableId },
        { label: "Pago", value: paymentMethod },
      ],
      confirmLabel: "Cobrar todo",
      danger: true,
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Cobrando mesa…",
      successMsg: "✓ Mesa cerrada",
      restaurantId,
      targetTab: "caja",
      targetTabLabel: "Cobrar Orden",
      run: async () => {
        try {
          const body: Record<string, unknown> = {
            tableId,
            cashRegisterId,
            paymentMethod,
          };
          if (paymentDetails) body.paymentDetails = paymentDetails;
          if (clientId) body.clientId = clientId;
          const { data } = await apiWithAuth.post(
            `/restaurants/${restaurantId}/orders/close-for-table`,
            body,
            ctx.accessToken,
          );
          emitCopilotMutation({ scope: "orders", action: "closed", restaurantId });
          return {
            ok: true as const,
            result: {
              ordersClosed: data?.ordersClosed ?? data?.count,
              total: data?.total,
            },
            humanSummary: `Mesa cerrada (${data?.count ?? 0} pedido(s)).`,
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const sendInvoiceSkill: Skill = {
  id: "sendInvoice",
  description:
    "Envía la factura de un pedido cerrado por email al cliente.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      orderId: { type: "string" },
      email: { type: "string" },
    },
    required: ["restaurantId", "orderId", "email"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const orderId = String(args.orderId ?? "");
    const email = String(args.email ?? "").trim();
    if (!restaurantId || !orderId || !email) return { ok: false, error: "Faltan datos." };

    const ok = await ctx.confirmation.confirm({
      title: "Enviar factura por email",
      details: [
        { label: "Pedido", value: orderId },
        { label: "Email", value: email },
      ],
      confirmLabel: "Enviar",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: `Enviando factura a ${email}…`,
      successMsg: "✓ Factura enviada",
      restaurantId,
      targetTab: "pedidos-completados",
      targetTabLabel: "Órdenes Completadas",
      run: async () => {
        try {
          await apiWithAuth.post(
            `/restaurants/${restaurantId}/orders/${orderId}/send-invoice`,
            { email },
            ctx.accessToken,
          );
          return {
            ok: true as const,
            result: { orderId, email },
            humanSummary: `Factura enviada a ${email}.`,
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const restoreProductSkill: Skill = {
  id: "restoreProduct",
  description:
    "Restaura un producto archivado (lo vuelve a mostrar en el catálogo).",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      productId: { type: "string" },
    },
    required: ["restaurantId", "productId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const productId = String(args.productId ?? "");
    if (!restaurantId || !productId) return { ok: false, error: "Faltan IDs." };

    const ok = await ctx.confirmation.confirm({
      title: "Restaurar producto",
      description: "El producto volverá a aparecer en el catálogo y la carta.",
      confirmLabel: "Restaurar",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Restaurando producto…",
      successMsg: "✓ Producto restaurado",
      restaurantId,
      targetTab: "productos",
      targetTabLabel: "Productos",
      run: async () => {
        try {
          await apiWithAuth.patch(
            `/restaurants/${restaurantId}/products/${productId}`,
            { isArchived: false },
            ctx.accessToken,
          );
          emitCopilotMutation({ scope: "products", action: "updated", restaurantId, id: productId });
          return { ok: true as const, result: { productId, restored: true }, humanSummary: "Producto restaurado." };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};
